import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { OrderContactDto, OrderContactPostRequest } from "../../../infrastructure/gfo_api/order_api";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import ErrorMessage from "../../Shared/ErrorMessage";
import InternationalNumberEntry from "../../Shared/InternationalNumberEntry";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

const CONTACT_TYPE_PRIVATE = "private";
const CONTACT_TYPE_COMPANY = "company";
const ADDRESS_TYPE_INVOICE = "invoice";

interface Props {
  addressType: string;
  address: OrderContactDto | null;
  title: string;
}

export default defineComponent({
  template: html,
  components: { ErrorMessage, InternationalNumberEntry },
  props: {
    addressType: {
      type: String,
      required: true,
    },
    address: {
      type: Object as () => OrderContactDto | null,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
  },
  setup(props: Props) {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);

    // vars und computed
    const generalError = ref("");
    // Fehler pro Feld, keyed wie das Request-Objekt (snake_case, wie vom Server zurückgemeldet)
    // ein Feld kann mehrere Fehlermeldungen haben
    const errors = ref<Record<string, string[]>>({});

    const contactType = ref<string>(CONTACT_TYPE_PRIVATE);
    const companyName = ref<string | null>(null);
    const uid = ref<string | null>(null);
    const gender = ref<string | null>(null);
    const firstName = ref<string | null>(null);
    const lastName = ref<string | null>(null);
    const birthdate = ref<string | null>(null);
    const street = ref<string | null>(null);
    const buildingNumber = ref<string | null>(null);
    const zip = ref<string | null>(null);
    const city = ref<string | null>(null);
    const countryCode = ref<string | null>(null);
    const email = ref<string | null>(null);
    const phone = ref<string | null>(null);
    const mobile = ref<string | null>(null);

    // eindeutige IDs, da diese Komponente mehrfach (invoice/delivery/technical) gleichzeitig gerendert wird
    const fieldId = (field: string) => `address-${props.addressType}-${field}`;

    // Geburtsdatum wird nur bei der Rechnungsadresse und nur für Privatpersonen erfasst
    const showBirthdate = computed(() => {
      return ADDRESS_TYPE_INVOICE == props.addressType && CONTACT_TYPE_PRIVATE == contactType.value;
    });

    // methods
    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        errors.value = {};
        generalError.value = "";
        if (orderService == null) {
          return;
        }
        const request: OrderContactPostRequest = {
          contact_type: contactType.value,
          company_name: CONTACT_TYPE_COMPANY == contactType.value ? companyName.value : null,
          uid: CONTACT_TYPE_COMPANY == contactType.value ? uid.value : null,
          first_name: firstName.value,
          last_name: lastName.value,
          gender: gender.value,
          street: street.value,
          building_number: buildingNumber.value,
          zip: zip.value,
          city: city.value,
          country_code: "CH",
          language: "de",
          email: email.value,
          phone: phone.value,
          fax: null,
          mobile: mobile.value,
          birthdate: showBirthdate.value ? birthdate.value : null,
        };
        try {
          await orderService.postAddress(props.addressType, request);
        } catch (err) {
          if (err instanceof ApiValidationError) {
            errors.value = { ...err.errors };
            generalError.value = err.title;
          } else {
            generalError.value = `${err}`;
          }
        }
      };
      pendingSave = run();
      await pendingSave;
    };

    const onContactTypeChange = async () => {
      if (CONTACT_TYPE_PRIVATE == contactType.value) {
        companyName.value = null;
        uid.value = null;
      } else {
        birthdate.value = null;
      }
      //await save();
    };

    const isValid = computed(() => {
      if (CONTACT_TYPE_COMPANY == contactType.value && !companyName.value) {
        return false;
      }
      if (!firstName.value || !lastName.value || !street.value || !zip.value || !city.value || !email.value) {
        return false;
      }
      return Object.values(errors.value).every((messages) => !messages || 0 == messages.length);
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      const fieldErrors: Record<string, string[]> = { ...errors.value };
      if (CONTACT_TYPE_COMPANY == contactType.value && !companyName.value) {
        fieldErrors.company_name = [t("Validation_Field_Required")];
      }
      if (!firstName.value) {
        fieldErrors.first_name = [t("Validation_Field_Required")];
      }
      if (!lastName.value) {
        fieldErrors.last_name = [t("Validation_Field_Required")];
      }
      if (!street.value) {
        fieldErrors.street = [t("Validation_Field_Required")];
      }
      if (!zip.value) {
        fieldErrors.zip = [t("Validation_Field_Required")];
      }
      if (!city.value) {
        fieldErrors.city = [t("Validation_Field_Required")];
      }
      if (!email.value) {
        fieldErrors.email = [t("Validation_Field_Required")];
      }
      errors.value = fieldErrors;
      return isValid.value;
    };

    onMounted(() => {
      contactType.value = props.address?.contact_type || CONTACT_TYPE_PRIVATE;
      companyName.value = props.address?.company_name ?? null;
      uid.value = props.address?.uid ?? null;
      gender.value = props.address?.gender ?? null;
      firstName.value = props.address?.first_name ?? null;
      lastName.value = props.address?.last_name ?? null;
      birthdate.value = props.address?.birthdate ?? null;
      street.value = props.address?.street ?? null;
      buildingNumber.value = props.address?.building_number ?? null;
      zip.value = props.address?.zip ?? null;
      city.value = props.address?.city ?? null;
      countryCode.value = props.address?.country_code || "CH";
      email.value = props.address?.email ?? null;
      phone.value = props.address?.phone ?? null;
      mobile.value = props.address?.mobile ?? null;
    });

    return {
      t,
      generalError,
      errors,
      contactType,
      companyName,
      uid,
      gender,
      firstName,
      lastName,
      birthdate,
      showBirthdate,
      street,
      buildingNumber,
      zip,
      city,
      countryCode,
      email,
      phone,
      mobile,
      fieldId,
      save,
      onContactTypeChange,
      isValid,
      validate,
    };
  },
} as any);
